import { useEffect, useRef } from 'react';
import { fetchPluginUpdateState } from '../../api/grpc/pluginUpdateClient';
import {
  usePluginUpdateStore,
  useUpdateBannerMode,
} from '../../stores/usePluginUpdateStore';

const POLL_INTERVAL_MS = 1500;
const ACTIVE_MODES = new Set(['downloading', 'verifying']);

/**
 * While the banner is showing an active install (downloading / verifying), poll
 * the plugin every 1.5s for a fresh snapshot so the progress bar and lifecycle
 * advance without user interaction. The interval lives entirely inside the
 * mounted banner's React lifecycle — it starts when the mode becomes active,
 * stops when it leaves those states, and is cleared on unmount, so there is
 * never a stray global timer. An in-flight guard drops overlapping ticks if a
 * poll outlives its interval.
 */
export function usePluginUpdatePolling(): void {
  const mode = useUpdateBannerMode();
  const applySnapshot = usePluginUpdateStore((s) => s.applySnapshot);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!ACTIVE_MODES.has(mode)) {
      return;
    }

    let cancelled = false;
    const tick = async () => {
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      try {
        const snapshot = await fetchPluginUpdateState();
        if (!cancelled && snapshot) {
          applySnapshot(snapshot);
        }
      } finally {
        inFlightRef.current = false;
      }
    };

    const intervalId = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [mode, applySnapshot]);
}
